import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { socialSignInProviders } from "features/auth/models";
import {
  verifyEmail,
  resetPasswordEmailVerify,
  getAuthUserProfile,
  signInUserViaSocial,
  signInUserViaEmail,
} from "redux/actions/auth";
import { StorageFields } from "utils/constants";
import { addToStorage } from "utils/storage/Local";

export enum Roles {
  expert = 1,
  student = 0,
}

export enum IIsExpertApproved {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = -1
}

export interface AuthUserCredentials {
  avatar: string;
  email: string;
  firstName: string;
  lastName: string;
  id: number | string;
  isAgree: number | boolean;
  isExpert: number | boolean;
  isVerified: number | boolean;
  socialId: number | string;
  socialType: socialSignInProviders;
  status: number | string;
  token?: string;
  logged: boolean;
  currentRole: Roles;
  isExpertApproved: IIsExpertApproved;
  expertId?: number;
  thumbnail: string;
  languageId?: number;
  countryId?: number;
  introduction?: string;
}

export interface IVerifyEmail {
  loading: boolean;
  success: boolean;
  message: any;
}

export interface AuthUser {
  authUser: AuthUserCredentials | null;
  verifyEmail: IVerifyEmail;
  resetPassword: any;
}

const initial: AuthUser = {
  authUser: null,
  verifyEmail: {
    loading: false,
    success: false,
    message: null,
  },
  resetPassword: null,
};

const handleAuthUser = (state: AuthUser, data: any) => {
  if (data) {
    const { user } = data;
    const userProfile: AuthUserCredentials = {
      token: user?.token || state?.authUser?.token,
      avatar: user?.avatar,
      thumbnail: user?.thumbnail,
      email: user?.email,
      firstName: user?.first_name,
      lastName: user?.last_name,
      id: user?.id,
      isAgree: user?.is_agree,
      isExpert: user?.is_expert,
      isVerified: user?.is_verified,
      socialId: user?.social_id,
      socialType: user?.social_type,
      status: user?.status,
      logged: true,
      isExpertApproved: user?.is_expert_approved,
      currentRole: state?.authUser?.currentRole || Roles.student,
      languageId: user?.language_id,
      countryId: user?.country_id,
      introduction: user?.introduction,
    };
    addToStorage(StorageFields.User, JSON.stringify(userProfile));

    return userProfile;
  }
  return null;
};

export const authSlice = createSlice({
  name: "auth",
  initialState: initial,
  reducers: {
    setAuthUser: (state, action: PayloadAction<AuthUserCredentials | null>) => {
      state.authUser = action.payload;
    },
    changeCurrentRole: (state, action: PayloadAction<Roles>) => {
      if (state.authUser) state.authUser.currentRole = action.payload;
    },
    logOut: (state) => {
      state.authUser = null;
    },
  },
  extraReducers: (builder) => {
    // verifyEmail
    builder.addCase(verifyEmail.pending, (state, action) => {
      state.verifyEmail.loading = true;
      state.verifyEmail.success = false;
      state.verifyEmail.message = null;
    });
    builder.addCase(verifyEmail.fulfilled, (state, action) => {
      state.verifyEmail.loading = false;
      state.verifyEmail.success = true;
    });
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.verifyEmail.loading = false;
    });

    // resetPasswordEmailVerify
    builder.addCase(resetPasswordEmailVerify.pending, (state, action) => {
      state.resetPassword = null;
    });
    builder.addCase(resetPasswordEmailVerify.fulfilled, (state, action) => {
      state.resetPassword = action.payload;
    });
    builder.addCase(resetPasswordEmailVerify.rejected, (state, action) => {
      state.resetPassword = null;
    });

    // getAuthUserProfile
    builder.addCase(getAuthUserProfile.fulfilled, (state, action) => {
      const formatedData = handleAuthUser(state, action.payload);
      state.authUser = formatedData;
    });
    builder.addCase(getAuthUserProfile.rejected, (state, action) => {
      state.authUser = null;
    });

    // signInUserViaSocial
    builder.addCase(signInUserViaSocial.pending, (state, action) => {
      state.authUser = null;
    });
    builder.addCase(signInUserViaSocial.fulfilled, (state, action) => {
      const formatedData = handleAuthUser(state, action.payload);
      state.authUser = formatedData;
    });
    builder.addCase(signInUserViaSocial.rejected, (state, action) => {
      state.authUser = null;
    });

    // signInUserViaEmail
    builder.addCase(signInUserViaEmail.pending, (state, action) => {
      state.authUser = null;
    });
    builder.addCase(signInUserViaEmail.fulfilled, (state, action) => {
      const formatedData = handleAuthUser(state, action.payload);
      state.authUser = formatedData;
    });
    builder.addCase(signInUserViaEmail.rejected, (state, action) => {
      state.authUser = null;
    });
  },
});

export const { setAuthUser, changeCurrentRole, logOut } = authSlice.actions;

export default authSlice.reducer;
